<template>
    <div>
        <DoctorAppointmentRoot></DoctorAppointmentRoot>
    </div>
</template>

<script setup>
const route = useRoute()
const router = useRouter()
const { setSEOMeta, setDoctorStructuredData, setLLMOptimizedMeta, setHreflangAlternates, generateSEOUrl } = useSEO()
// Emit hreflang alternates for all locales
try { setHreflangAlternates() } catch {}
const {searchQuery, searchResults, getSearchResults, listFocusIndex, doctorDetails} = useSearch()
const {
    targetDoctorId,
    targetFacilityId,
    targetDateTime,
    ptData,
    submitAppointmentRequest,
    selectedApptDate,
    getAppointmentSlots,
    AppointmentSlots,
    setAppointmentSlots,
    selectedAppointmentSlot,
    setSelectedAppointmentSlot,
    setTargetDoctorId,
    setTargetFacilityId,
} = usePatientAppointmentRequest()

const specialty = route.params.specialty
const doctor_name = route.params.slug?.replace(/医生/g, '')

// Fetch doctor details; prefer direct id lookup if provided
let doctorData = null
const idParam = (route.query && typeof route.query.id === 'string') ? route.query.id : ''

if (idParam) {
  try {
    console.log(`[Appointment Page] Attempting ID lookup: id=${idParam}`)
    doctorData = await $fetch(`/api/doctor/getDetails?id=${idParam}`)
    console.log('[Appointment Page] ID lookup successful')
  } catch (error) {
    console.log('[Appointment Page] ID lookup failed:', error)
  }
}

if (!doctorData && route.params.slug) {
  // Traditional format: /doctor/specialty/doctor-name/appointment
  try {
    console.log(`[Appointment Page] Attempting direct lookup: specialty=${specialty}, doctor_name=${doctor_name}`)
    doctorData = await $fetch(`/api/doctor/getDetails?specialty=${specialty}&doctor_name=${doctor_name}`)
    console.log('[Appointment Page] Direct lookup successful')
  } catch (error) {
    console.log('[Appointment Page] Direct lookup failed, trying Elasticsearch fallback')
    try {
      doctorData = await $fetch(`/api/doctor/getDetails?specialty=${specialty}&doctor_name=`)
    } catch (fallbackError) {
      console.error('[Appointment Page] Both direct lookup and Elasticsearch fallback failed:', fallbackError)
      doctorData = null
    }
  }
} else {
  // Slug format: /doctor/slug-with-npi/appointment
  try {
    console.log(`[Appointment Page] Attempting slug lookup: slug=${specialty}`)
    doctorData = await $fetch(`/api/doctor/getDetails?slug=${specialty}`)
  } catch (error) {
    console.error('[Appointment Page] Slug lookup failed:', error)
    doctorData = null
  }
}

// Enhanced error handling for doctor data
if (!doctorData || !doctorData.provider) {
  console.warn('[Appointment Page] Missing or invalid doctor data; applying fallback SEO')
  doctorDetails.value = null
} else {
  doctorDetails.value = doctorData
}

// Safe assignment with graceful fallbacks
if (doctorData?.provider?._id) {
  try { setTargetDoctorId(String(doctorData.provider._id)) } catch { setTargetDoctorId('') }
}
if (doctorData?.facilities?.[0]?._id) {
  try { setTargetFacilityId(String(doctorData.facilities[0]._id)) } catch { setTargetFacilityId('') }
}

// Enhanced SEO for appointment page with comprehensive error handling
if (doctorDetails.value?.provider) {
  const provider = doctorDetails.value.provider
  const facility = doctorDetails.value.facilities?.[0]
  const aigcContent = provider.aigc_v3?.chat_response
  
  // Safe data extraction with fallbacks
  const doctorName = provider.cn_name || provider.en_firstname + ' ' + provider.en_lastname || '医生'
  const englishName = provider.en_firstname && provider.en_lastname ? 
    `${provider.en_firstname} ${provider.en_lastname}` : 
    (provider.en_firstname || provider.en_lastname || '')
  const specialtyChinese = provider.cn_specialty_1 || provider.en_specialty_1 || '专科'
  const specialtyEnglish = provider.en_specialty_1 || provider.cn_specialty_1 || 'Specialty'
  const facilityAddress = facility?.cn_full_address || facility?.en_full_address || facility?.mapbox_result?.address || '纽约'
  const phoneNumber = facility?.telephone_tracking_hys_1 || facility?.telephone || ''
  const npiNumber = facility?.npi || ''
  
  // Bilingual meta title with safe fallbacks
  const metaTitle = `在线预约挂号 ${doctorName}${englishName ? `(${englishName})` : ''} | 纽约${specialtyChinese}${specialtyEnglish !== specialtyChinese ? `(${specialtyEnglish})` : ''}医生 | 北美好医生`
  
  // Enhanced description with comprehensive error handling
  const metaDescription = aigcContent?.seo_metadescription_chinese || 
    `在线预约挂号${doctorName}${englishName ? `(${englishName})` : ''}，纽约${specialtyChinese}医生。诊所位于${facilityAddress}，${phoneNumber ? `预约电话${phoneNumber}，` : ''}${npiNumber ? `NPI执照号${npiNumber}。` : ''}接受大部分美国医疗保险，提供自费优惠。${englishName ? `Book appointment with ${englishName}, ${specialtyEnglish} specialist in New York.` : ''}`

  // Comprehensive keywords with safe data extraction
  const baseKeywords = [
    doctorName,
    specialtyChinese,
    '在线预约挂号',
    'online appointment booking',
    '纽约医生',
    'New York doctor',
    '华人医生',
    'Chinese doctor',
    '医疗保险',
    'health insurance',
    '预约挂号',
    'appointment booking'
  ]

  // Add English names if available
  if (englishName) {
    baseKeywords.push(englishName)
  }
  if (specialtyEnglish && specialtyEnglish !== specialtyChinese) {
    baseKeywords.push(specialtyEnglish)
  }

  // Add AIGC keywords if available
  const seoKeywords = aigcContent?.seo_keywords_chinese?.length ? 
    [...aigcContent.seo_keywords_chinese, ...baseKeywords] : baseKeywords

  // Compute preferred canonical URL
  let canonicalUrl
  try {
    const rc = useRuntimeConfig()
    const baseUrl = rc['site']?.['url'] || rc.public['siteUrl'] || 'https://haodrs.com'
    const slugFromProvider = provider.slug_cn || provider.slug || ''
    if (slugFromProvider) {
      canonicalUrl = `${baseUrl}/doctor/${slugFromProvider}/appointment`
    } else if (englishName || specialtyEnglish) {
      const npi = facility?.npi ? String(facility.npi) : ''
      const genSlug = generateSEOUrl(`${englishName || doctorName} ${specialtyEnglish || ''} ${npi}`.trim())
      canonicalUrl = `${baseUrl}/doctor/${genSlug}/appointment`
    }
  } catch {}

  // Set comprehensive SEO meta with error handling
  try {
    setSEOMeta({
      title: metaTitle,
      description: metaDescription,
      keywords: seoKeywords,
      ogImage: '/HYS-logo-icon-06.png',
      author: doctorName,
      locale: 'zh-Hans',
      ...(canonicalUrl ? { canonicalUrl } : {})
    })
  } catch (seoError) {
    console.error('[Appointment Page] Failed to set SEO meta:', seoError)
    // Don't throw - allow page to continue loading
  }

  // Set healthcare-specific structured data with error handling
  try {
    const structuredData = {
      '@type': 'Physician',
      name: doctorName,
      description: metaDescription,
      image: '/HYS-logo-icon-06.png',
      medicalSpecialty: [specialtyChinese, specialtyEnglish].filter(Boolean),
      memberOf: {
        '@type': 'MedicalOrganization',
        name: '北美好医生'
      },
      acceptsReservations: true,
      paymentAccepted: ['Insurance', 'Cash', 'Credit Card'],
      priceRange: '$$',
      availableLanguage: ['Chinese', 'English'],
      // Additional appointment-specific structured data
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Appointment Services',
        itemListElement: [
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Online Appointment Booking',
              description: 'Convenient online appointment booking service'
            }
          }
        ]
      }
    }

    // Add optional fields only if they exist
    if (englishName) {
      structuredData.alternateName = englishName
    }
    if (phoneNumber) {
      structuredData.telephone = phoneNumber
      structuredData.memberOf.telephone = phoneNumber
    }
    if (facility?.contact_email) {
      structuredData.email = facility.contact_email
    }
    if (facility?.en_full_address) {
      structuredData.address = {
        '@type': 'PostalAddress',
        streetAddress: facility.en_full_address.split(',')[0] || '',
        addressLocality: facility.city || 'New York',
        addressRegion: facility.state || 'NY',
        postalCode: facility.zip_code || '',
        addressCountry: 'US'
      }
    }

    setDoctorStructuredData(structuredData)
  } catch (structuredDataError) {
    console.error('[Appointment Page] Failed to set structured data:', structuredDataError)
    // Don't throw - allow page to continue loading
  }

  // Set LLM-optimized metadata with error handling
  try {
    const llmKeyTerms = aigcContent?.seo_keywords_chinese?.length ? 
      [...aigcContent.seo_keywords_chinese, doctorName, '在线预约挂号', 'appointment booking'] :
      [
        doctorName,
        specialtyChinese,
        '在线预约挂号',
        'appointment booking',
        '纽约医生',
        '华人医疗',
        '医生预约'
      ]

    setLLMOptimizedMeta({
      contentType: 'appointment-booking',
      primaryTopic: `${specialtyChinese}医生预约服务`,
      keyTerms: llmKeyTerms,
      contentSummary: aigcContent?.long_brief_chinese || 
        `${doctorName}医师的在线预约服务，包括预约流程、诊所信息、联系方式和保险信息`,
      medicalSpecialties: [specialtyChinese, specialtyEnglish].filter(Boolean),
      targetAudience: `需要${specialtyChinese}医疗服务的华人患者和英语用户`,
      // Include AIGC services and conditions for enhanced LLM understanding
      services: aigcContent?.common_services_provided?.map(s => s.service).filter(Boolean) || [],
      conditions: aigcContent?.common_treated_conditions?.map(c => c.condition).filter(Boolean) || []
    })
  } catch (llmError) {
    console.error('[Appointment Page] Failed to set LLM meta:', llmError)
    // Don't throw - allow page to continue loading
  }
}

// Fallback SEO if doctor data is not available or incomplete
if (!doctorDetails.value?.provider) {
  try {
    setSEOMeta({
      title: '在线预约挂号 - 北美好医生 | Online Appointment Booking',
      description: '北美好医生提供便捷的在线预约挂号服务，连接患者与优质华人医疗资源。支持多种医疗保险，提供自费优惠。Book online appointments with Chinese doctors in New York.',
      keywords: ['在线预约挂号', '医生预约', '华人医疗', '医疗保险', '纽约医生', 'online appointment', 'Chinese doctor New York'],
      ogImage: '/HYS-logo-icon-06.png',
      locale: 'zh-Hans'
    })
  } catch (fallbackSeoError) {
    console.error('[Appointment Page] Failed to set fallback SEO:', fallbackSeoError)
    // Don't throw - allow page to continue loading
  }
}

</script>